import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DsButton, DsInput } from "../../../design-system";

const selectClass =
  "ds-input h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]";

export default function InviteUserModal({ open, roles = [], loading, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [roleCode, setRoleCode] = useState("EMPLOYEE");
  const firstRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setName("");
    setEmail("");
    setRoleCode(
      String(roles.find((r) => String(r.code).toUpperCase() === "EMPLOYEE")?.code || "EMPLOYEE").toUpperCase()
    );
    const t = setTimeout(() => firstRef.current?.focus(), 50);
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open, roles, loading, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="urm-invite-title"
        className="flex w-full max-w-md flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-2xl"
      >
        <div className="border-b border-[hsl(var(--ds-border))] px-5 py-4">
          <h3 id="urm-invite-title" className="ds-h4 text-[hsl(var(--ds-fg))]">
            Invite user
          </h3>
        </div>
        <form
          className="grid gap-3 px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit?.({ name: name.trim(), email: email.trim(), roleCode });
          }}
        >
          <div className="grid gap-1.5">
            <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]" htmlFor="urm-invite-name">
              Name <span className="text-[hsl(var(--ds-danger))]">*</span>
            </label>
            <DsInput
              ref={firstRef}
              id="urm-invite-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="grid gap-1.5">
            <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]" htmlFor="urm-invite-email">
              Email <span className="text-[hsl(var(--ds-danger))]">*</span>
            </label>
            <DsInput
              id="urm-invite-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="grid gap-1.5">
            <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]" htmlFor="urm-invite-role">
              Role
            </label>
            <select
              id="urm-invite-role"
              className={selectClass}
              value={roleCode}
              disabled={loading}
              onChange={(e) => setRoleCode(e.target.value)}
            >
              {(roles || [])
                .filter((r) => r.active !== false)
                .map((r) => (
                  <option key={r.id} value={String(r.code || "").toUpperCase()}>
                    {r.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="mt-2 flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] pt-3">
            <DsButton type="button" variant="outline" size="sm" onClick={onClose} disabled={loading}>
              Cancel
            </DsButton>
            <DsButton type="submit" size="sm" loading={loading}>
              {loading ? "Sending…" : "Send invite"}
            </DsButton>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
